import { queryOptions } from '@tanstack/react-query'
import { apiV1 } from '@/shared/api/apiPrefix'
import { meProfileResponseSchema, type UserProfileData } from '@/shared/schemas/meApi.schema'
import { isUnauthenticatedProblem, redirectToLoginAfterUnauthenticated } from '@/shared/lib/authSessionCleanup'

/** Canonical TanStack cache key for `GET /api/v1/me` (requireAuth, brand shell, profile). */
export const meQueryKey = ['auth', 'me'] as const

/**
 * Fetches `/me` without triggering mid-session 401 redirect side effects - safe for
 * route guards and shared cache seeding; other API calls still handle expiry via httpClient.
 */
type FetchMeProfileOptions = {
  redirectOnUnauthenticated?: boolean
}

export async function fetchMeProfile(options: FetchMeProfileOptions = {}): Promise<UserProfileData> {
  const redirectOnUnauthenticated = options.redirectOnUnauthenticated ?? true
  const baseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined
  const url = `${baseUrl ?? ''}${apiV1('/me')}`

  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })

  if (!res.ok) {
    let body: unknown
    try {
      body = await res.json()
    } catch {
      body = null
    }
    if (redirectOnUnauthenticated && res.status === 401 && isUnauthenticatedProblem(body)) {
      redirectToLoginAfterUnauthenticated()
    }
    throw new Error(`HTTP ${res.status}`)
  }

  return meProfileResponseSchema.parse(await res.json()).data
}

export const meQueryOptions = queryOptions({
  queryKey: meQueryKey,
  queryFn: () => fetchMeProfile(),
  staleTime: 60_000,
})
